import React from 'react';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { createSelector } from 'reselect';
import { get, isEmpty } from 'lodash';

import { withStyles } from '@material-ui/core/styles';
import {
  Button,
  Card,
  Chip,
  Grid,
  IconButton,
  Modal,
  TextField,
  Typography,
} from '@material-ui/core';
import { Add as AddIcon } from '@material-ui/icons';

import { ConfirmationPopup } from '../../app/components';
import QuestionCard from './cards/QuestionCard';
import { loadTexts } from '../../config/texts';

import {
  fetchSurveyQuestions,
  addSurveyQuestion,
  updateSQuestion,
  removeSQuestion,
} from './actions';

const TEXT_KEY = "survey";

const styles = theme => ({
  card: {
    display: 'flex',
    padding: theme.spacing.unit * 2,
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modal: {
    position: 'absolute',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    overflow: 'auto',
    width: theme.spacing.unit * 100,
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: theme.spacing.unit,
  },
  modalContent: {
    padding: theme.spacing.unit * 2,
  },
  chip: {
    margin: theme.spacing.unit * 0.5,
  },
  addOptionContainer: {
    display: 'flex',
  },
});

class Survey extends React.Component {
  state = {
    open: false,
    openDeleteConfirmation: false,
    questionToDelete: '',
    addQuestion: '',
    addOption: '',
    addOptions: [],
    texts: loadTexts(TEXT_KEY, this.props.locale)
  };

  componentDidMount() {
    this.props.fetchSurveyQuestions({ organization: this.props.selectedOrganization });
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.selectedOrganization !== this.props.selectedOrganization)
      this.props.fetchSurveyQuestions({ organization: nextProps.selectedOrganization });
  }

  _onClose = () => this.setState({ open: false });
  _onOpen = () => this.setState({ open: true });
  _onCancel = () => this.setState({ open: false, addQuestion: '', addOptions: [], addOption: '' });
  _onAddOption = () => {
    if (!this.state.addOption.trim()) return;
    this.setState({
      addOption: '',
      addOptions: [...this.state.addOptions, this.state.addOption],
    });
  };
  _onKeyPress = event => {
    if (event.key === 'Enter') {
      this._onAddOption();
    }
  };
  _onDeleteOption = option => {
    this.setState(state => ({
      addOptions: state.addOptions.filter(opt => opt !== option),
    }));
  };
  _onSubmit = async () => {
    this.setState({ open: false });
    await this.props.addSurveyQuestion({ sQuestionInput: {
      _organization: this.props.selectedOrganization,
      question: this.state.addQuestion,
      options: this.state.addOptions,
    } });
    this.props.fetchSurveyQuestions({ organization: this.props.selectedOrganization });
    this._onCancel();
  }
  _onUpdateQuestion = async ({ _id, newQuestion, newOptions }) => {
    await this.props.updateSQuestion({
      questionID: _id,
      question: newQuestion,
      options: newOptions,
    });
    return this.props.fetchSurveyQuestions({ organization: this.props.selectedOrganization });
  }
  _onDeleteQuestion = async () => {
    this.setState({ openDeleteConfirmation: false, questionToDelete: '' });
    if (!this.state.questionToDelete) return;
    await this.props.removeSQuestion({ questionID: this.state.questionToDelete });
    return this.props.fetchSurveyQuestions({ organization: this.props.selectedOrganization });
  };
  _onOpenDeleteConfirmation = ({ _id }) => {
    this.setState({ openDeleteConfirmation: true, questionToDelete: _id });
  }
  _onCloseDeleteConfirmation = () => this.setState({ openDeleteConfirmation: false });

  render() {
    const {
      classes,
      organizations,
      selectedOrganization,
      questions,
      questionsKeyed,
      responses,
    } = this.props;
    const { open, addQuestion, addOption, addOptions, openDeleteConfirmation, texts } = this.state;
    const org = organizations.find(o => o._id === selectedOrganization) || {};
    return (
      <React.Fragment>
        <Grid container spacing={16}>
          {questions.map(qid => {
            const q = questionsKeyed[qid];
            const { labels, data } = responses[qid];
            return (
              <Grid key={qid} item>
                <QuestionCard
                  questionID={q._id}
                  question={q.question}
                  options={q.options}
                  labels={labels}
                  data={data}
                  onUpdate={this._onUpdateQuestion}
                  onDelete={this._onOpenDeleteConfirmation}
                />
              </Grid>
            );
          })}
          <Grid item>
            <Card className={classes.card}>
              <IconButton onClick={this._onOpen}>
                <AddIcon />
              </IconButton>
              <Typography>
                {texts.title.add_question}
              </Typography>
            </Card>
          </Grid>
        </Grid>
        <Modal open={open} onClose={this._onClose}>
          <Card className={classes.modal}>
            <div className={classes.modalContent}>
              <Typography>
                {`${texts.title.organization}: ${org.name || texts.title.unknown}`}
              </Typography>
              <TextField
                id="add_question"
                label={texts.label.question}
                value={addQuestion}
                onChange={event => this.setState({ addQuestion: event.target.value })}
                fullWidth
              />
              <div className={classes.addOptionContainer}>
                <TextField
                  id="add_option"
                  label={texts.label.add_option}
                  value={addOption}
                  onChange={event => this.setState({ addOption: event.target.value })}
                  onKeyPress={this._onKeyPress}
                />
                <Button onClick={this._onAddOption} color="primary">
                  {texts.button_text.add}
                </Button>
              </div>
              <div>
                {addOptions.map((option, i) => (
                  <Chip
                    key={i}
                    label={option}
                    onDelete={() => this._onDeleteOption(option)}
                    className={classes.chip}
                  />
                ))}
              </div>
            </div>
            <div className={classes.buttonContainer}>
              <Button onClick={this._onCancel}>
                {texts.button_text.cancel}
              </Button>
              <Button
                disabled={!addQuestion || addOptions.length < 1}
                onClick={this._onSubmit}
                color="primary"
              >
                {texts.button_text.submit}
              </Button>
            </div>
          </Card>
        </Modal>
        <ConfirmationPopup
          open={openDeleteConfirmation}
          title={texts.alert.title}
          subtitle={texts.alert.subtitle}
          affirmativeButtonText={texts.alert.button_text}
          onClose={this._onCloseDeleteConfirmation}
          onSubmit={this._onDeleteQuestion}
        />
      </React.Fragment>
    );
  }
}

const mapStateToProps = state => ({
  locale: state.appState.locale,
  selectedOrganization: get(state, 'auth.selectedOrganization'),
  organizations: get(state, 'auth.data.user.organizations'),
  questions: get(state, 'survey.main.data.order') || [],
  questionsKeyed: get(state, 'survey.main.data.keyed') || {},
  responses: createSelector(
    state => state.survey.main.data.keyed || {},
    keyed => {
      if (isEmpty(keyed)) return {};
      return Object.keys(keyed).reduce((acc, key) => {
        const { responses } = keyed[key];
        const grouped = responses.reduce((acc2, { response }) => {
          const current = acc2[response] || 0;
          return { ...acc2, [response]: current + 1 };
        }, {});
        const empty = isEmpty(grouped)
        const labels = empty ? ['No Data'] : Object.keys(grouped);
        return { ...acc, [key]: {
          labels,
          data: empty ? [1] : labels.map(l => grouped[l]),
        } };
      }, {});
    }
  )(state),
});

const mapDispatchToProps = dispatch => ({
  fetchSurveyQuestions: params => dispatch(fetchSurveyQuestions(params)()),
  addSurveyQuestion: params => dispatch(addSurveyQuestion(params)()),
  updateSQuestion: params => dispatch(updateSQuestion(params)()),
  removeSQuestion: params => dispatch(removeSQuestion(params)()),
});

export default compose(
  withStyles(styles, { name: 'Survey' }),
  connect(mapStateToProps, mapDispatchToProps)
)(Survey);



// WEBPACK FOOTER //
// ./src/modules/survey/Scene.js